import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';

/* External */
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';

/* Material UI */
import { makeStyles } from '@material-ui/core/styles';
import { withStyles } from '@material-ui/core';

/* Project */
import { selectState, actionProps } from 'utils/reduxActions';
import { homeActions } from 'screens/Home/reducer';
import { ReactReduxContext } from 'config/configureStore';
import connectSaga from 'utils/saga';
import { clone, falsy, onChangeFnc, truthty, validateFile } from 'utils/functions';
import { parseToDate } from 'utils/DateFormat';
import { processesActions } from 'screens/Processes/reducer';
import ProcessesComponent from './components';
import saga from './saga';
import styles from './styles';
import { defaultProcesses } from 'utils/defaultProcesses';
import { useAutomation } from './../../utils/hooks/useAutomationPermission';
import {
  shouldDefaultProcessesStatusToOpen,
  shouldUseCallOffInAutomationModals,
} from '../../utils/tenantFeatures';

const useStyles = makeStyles(styles);

const getDefaultFilters = () => ({
  // TODO: CAMBIAR A MES POSTERIOR DE OPTIMIZAR LA PAGINACIÓN EN EL BACKEND.
  // fromDate: parseToDate(new Date()).beginningOfMonth.toISOString(),
  // toDate: parseToDate(new Date()).endOfMonth.toISOString(),
  fromDate: parseToDate(
    new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
  ).beginningOfDay.toISOString(),
  toDate: parseToDate(new Date()).endOfDay.toISOString(),
  processTypeId: '',
  workShiftTypeId: '',
  processCategoryId: '',
  productId: '',
  number: '',
  status: shouldDefaultProcessesStatusToOpen() ? 'open' : '',
  equipmentId: '',
  employeeIds: [],
  supplierId: '',
  lotId: '',
  receiptLotId: '',
  callOff: '',
});

const defaultPagination = {
  page: 0,
  rowsPerPage: 10,
  count: 0,
};

function Processes(props) {
  const { t } = useTranslation();
  const { hasAutomation } = useAutomation();
  const defaultFilters = getDefaultFilters();
  const shouldShowCallOffColumn = hasAutomation && shouldUseCallOffInAutomationModals();

  const headerList = [
    { name: t('N°'), width: '110px', mobileOrder: 1 },
    { name: shouldShowCallOffColumn ? 'CallOff' : t('Lot number'), width: '130px', mobileOrder: 2 },
    { name: t('Supplier'), width: '150px', mobileOrder: 3 },
    { name: t('Category'), width: '120px', mobileOrder: 4 },
    { name: t('Type'), width: '170px', mobileOrder: 5 },
    { name: t('Product'), width: '160px', mobileOrder: 6 },
    { name: t('Variety'), width: '160px', mobileOrder: 7 },
    { name: t('Date'), width: '150px', mobileOrder: 8 },
    { name: t('Created by'), width: '130px', mobileOrder: 9 },
    { name: t('Entries'), width: '120px', mobileOrder: 10 },
    { name: t('Outputs'), width: '120px', mobileOrder: 11 },
    { name: t('Waste'), width: '120px', mobileOrder: 12 },
    { name: '', width: '50px', mobileOrder: 13 },
  ];

  const [processList, setProcessList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [reclassifyModalOpen, setReclassifyModalOpen] = useState(false);
  const [reclassifyProcess, setReclassifyProcess] = useState(null);
  const {
    objects,
    filter,
    formIsOpen,
    actions,
    formControls,
    edit,
    invalidControls,
    deleteIsOpen,
    employeeCostsModal,
    file,
    updatedProcesses,
    processesIds,
    loaders,
    pagination,
    loadingProcess,
    socketId,
    suppliersByProcess,
    toEditId,
    toDeleteId,
    history,
    device,
  } = props;

  useEffect(() => {
    setProcessList(
      processesIds
        .map((id) => objects.processes[id])
        .filter((process) => truthty(process))
        .sort((a, b) => b.id - a.id),
    );
  }, [processesIds.length, objects]);

  useEffect(() => {
    const getPagination = pagination.page !== 0 ? pagination : defaultPagination;
    actions.getStateFromApi({ ...filter, hasAutomation: true }, getPagination);
    actions.getAllReceiptLots();
    // eslint-disable-next-line
  }, [actions]);

  // #region  --------------- Functions ---------------
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function toggleForm() {
    actions.toggleForm('formIsOpen');
  }

  function toggleEmployeeCostsModal() {
    actions.toggleForm('employeeCostsModal');
  }

  function closeForm() {
    actions.toggleForm('deleteIsOpen');
  }

  function toggleDelete() {
    handleClose();
    actions.toggleDelete(anchorEl.dataset.id);
  }

  function toggleEdit() {
    handleClose();
    const item = Object.values(objects.processes).find(
      (it) => it.id === Number(anchorEl.dataset.id),
    );

    const newItem = { ...item, processModules: item.modules };
    delete newItem.modules;

    actions.toggleEdit(newItem);
  }

  function changeControls(event, date) {
    onChangeFnc(actions.changeControls, event, date);
  }

  function submit() {
    if (falsy(formControls.processTypeId) || falsy(formControls.workShiftTypeId)) {
      actions.setInvalidControls({
        processTypeId: falsy(formControls.processTypeId),
        workShiftTypeId: falsy(formControls.workShiftTypeId),
      });
    } else if (edit) {
      actions.editProcess(toEditId, { ...formControls });
    } else {
      actions.createProcess(formControls);
    }
  }

  function submitDelete() {
    actions.deleteProcess(toDeleteId);
  }

  function onFilterChange(event, date) {
    let input;
    let value;
    if (event.autocomplete) {
      input = event.input;
      value = event.value;
    } else {
      event.persist();
      input = falsy(event.target.type) ? event.currentTarget.id : event.target.id;
      value = event.target.type === 'date' ? date : event.target.value;
    }

    actions.changeFilter({
      [input]: value,
    });
  }

  function filterProcesses() {
    actions.getStateFromApi(filter, { ...defaultPagination });
  }

  function clearFilters() {
    actions.changeFilter({ ...defaultFilters });
    actions.paginationControls(defaultFilters, { ...defaultPagination });
  }

  function processOnClick(id) {
    return () => history.push(`/process/${id}`);
  }

  function openReclassifyModal(process) {
    setReclassifyProcess(process);
    setReclassifyModalOpen(true);
  }

  function closeReclassifyModal() {
    setReclassifyModalOpen(false);
    setReclassifyProcess(null);
  }

  function submitReclassifySpeciesDispatch(controls) {
    if (!reclassifyProcess?.receiptId) return;

    actions.reclassifySpeciesDispatch(
      reclassifyProcess.receiptId,
      {
        ...controls,
        lotId: reclassifyProcess.lotId,
      },
      (error) => {
      if (!error) {
        closeReclassifyModal();
        actions.getStateFromApi(filter, { ...pagination });
      }
      },
    );
  }

  function changeEmployeeCostsFile(e) {
    const blob = e.target.files[0];
    const validFile = validateFile(blob, { extensions: ['xlsx', 'xls'] });
    if (validFile.status) {
      actions.changeEmployeeCostsFile(e.target.files[0]);
    } else {
      actions.employeeCostsRejected();
    }

    // eslint-disable-next-line no-param-reassign
    e.target.value = null;
  }

  function submitEmployeeCosts() {
    actions.submitEmployeeCosts(file, processesIds);
  }

  const classes = useStyles();

  // #endregion   --------------- Functions ---------------
  return (
    <ProcessesComponent
      classes={classes}
      filter={filter}
      onFilterChange={onFilterChange}
      objects={objects}
      toggleEdit={toggleEdit}
      toggleDelete={toggleDelete}
      processList={processList}
      formIsOpen={formIsOpen}
      toggleForm={toggleForm}
      changeControls={changeControls}
      actions={actions}
      formControls={formControls}
      edit={edit}
      submit={submit}
      invalidControls={invalidControls}
      filterProcesses={filterProcesses}
      clearFilters={clearFilters}
      submitDelete={submitDelete}
      closeForm={closeForm}
      deleteIsOpen={deleteIsOpen}
      handleClick={handleClick}
      handleClose={handleClose}
      anchorEl={anchorEl}
      processOnClick={processOnClick}
      toggleEmployeeCostsModal={toggleEmployeeCostsModal}
      employeeCostsModal={employeeCostsModal}
      changeEmployeeCostsFile={changeEmployeeCostsFile}
      submitEmployeeCosts={submitEmployeeCosts}
      file={file}
      updatedProcesses={updatedProcesses}
      loaders={loaders}
      pagination={pagination}
      loadingProcess={loadingProcess}
      headerList={headerList}
      socketId={socketId}
      suppliersByProcess={suppliersByProcess}
      hasAutomation={hasAutomation}
      device={device}
      role={props.role}
      reclassifyModalOpen={reclassifyModalOpen}
      reclassifyProcess={reclassifyProcess}
      closeReclassifyModal={closeReclassifyModal}
      openReclassifyModal={openReclassifyModal}
      submitReclassifySpeciesDispatch={submitReclassifySpeciesDispatch}
    />
  );
}

const withSaga = connectSaga({ key: 'sagaProcesses', saga });

const withConnect = connect(
  selectState(
    'processes.employeeCostsModal',
    'processes.updatedProcesses',
    'processes.invalidControls',
    'processes.processesIds',
    'processes.suppliersByProcess',
    'processes.formControls',
    'processes.deleteIsOpen',
    'processes.toDeleteId',
    'processes.formIsOpen',
    'processes.toEditId',
    'processes.filter',
    'processes.file',
    'processes.pagination',
    'processes.loadingProcess',
    'processes.edit',
    'app.loaders',
    'app.objects',
    'app.socketId',
    'app.device',
    'app.role',
  ),
  actionProps(clone(clone.OBJECT, homeActions, processesActions)),
  null,
  { context: ReactReduxContext },
);

export default compose(withStyles(styles), withRouter, withConnect)(withSaga(Processes));
